import {ReportType} from '../../../types/report-type'

export type FilterOption = {value: string; label: string}
export type FilterField = {
  key: string
  label: string
  kind: 'text' | 'single' | 'multi' | 'date' | 'toggle'
  section: string
  source?: string
  options?: FilterOption[]
  wire?: 'options' | 'flag'
  initial?: string | string[]
  required?: boolean
  when?: (values: Record<string, any>) => boolean
}
export type ReportFilterSchema = {title: string; fields: FilterField[]; monthly?: boolean}
export const choices = (values: string[]): FilterOption[] =>
  values.map((value) => ({value, label: value}))
const text = (key: string, label: string, section = 'Identificação'): FilterField => ({
  key,
  label,
  kind: 'text',
  section,
})
const multi = (
  key: string,
  label: string,
  source?: string,
  options?: FilterOption[],
  section = 'Seleções'
): FilterField => ({key, label, source, options, kind: 'multi', section})
const entity = (key: string, label: string): FilterField => ({
  ...multi(key, label, key),
  wire: 'options',
})
const flag = (key: string, label: string, yes = 'SIM', no = 'NAO'): FilterField => ({
  ...multi(key, label, undefined, [
    {value: yes, label: 'Sim'},
    {value: no, label: 'Não'},
  ]),
  wire: 'flag',
})
const date = (key: string, label: string): FilterField => ({
  key,
  label,
  kind: 'date',
  section: 'Período',
})
const distributor = entity('distributors', 'Distribuidor')
const industry = entity('industries', 'Indústria')
const region = entity('regions', 'Regional')
const bu = {...entity('bu', 'Unidade de Negócio'), source: 'businessUnits'}
const cnpj = text('cnpj', 'CNPJ')
const UF = multi(
  'UF',
  'UF',
  undefined,
  choices(
    'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(' ')
  )
)
const period = [date('dtstart', 'Data Início'), date('dtend', 'Data Término')]
const dateStatuses = [
  {value: 'captado', label: 'Captado'},
  {value: 'aprovado', label: 'Aprovado'},
  {value: 'faturado', label: 'Faturado'},
  {value: 'entregue', label: 'Entregue'},
  {value: 'cancelado', label: 'Cancelado'},
]
const reference = (options = dateStatuses, initial = 'captado'): FilterField => ({
  ...multi('status', 'Referência de data', undefined, options, 'Período'),
  kind: 'single',
  initial,
  required: true,
})
const detail = (field: FilterField): FilterField => ({
  ...field,
  when: (values) => values.groupBySKU !== 'SIM',
})
const orderFields = (promotions = false): FilterField[] => [
  reference([...dateStatuses, {value: 'apurado', label: 'Apurado'}]),
  ...period,
  {key: 'groupBySKU', label: 'Agrupado por Pedido', kind: 'toggle', section: 'Apresentação'},
  cnpj,
  text('idPedido', 'ID do Pedido'),
  detail(text('idSKU', 'SKU')),
  detail(text('produto', 'Nome do Produto')),
  detail(text('grupo', 'Grupo')),
  detail(text('familiaProduto', 'Família Produto')),
  ...(promotions
    ? [detail(text('regraPromocao', 'Nome Promoção')), detail(text('idRegra', 'ID Regra'))]
    : []),
  distributor,
  detail(industry),
  detail(region),
  ...(promotions
    ? []
    : [
        text('meioPagamento', 'Meio de Pagamento'),
        text('prazoPagamento', 'Prazo de Pagamento'),
        multi(
          'origemPedido',
          'Origem Pedido',
          undefined,
          choices(['desktop', 'mobile', 'app', 'weduu']).map((o) => ({
            ...o,
            label: o.value === 'app' ? 'App' : o.value[0].toUpperCase() + o.value.slice(1),
          }))
        ),
        multi('tipoVenda', 'Tipo Venda', undefined, [
          {value: 'organica', label: 'Orgânica'},
          {value: 'assistida', label: 'Assistida'},
        ]),
      ]),
]
const product = (key: string, label: string, source = key): FilterField => ({
  ...multi(key, label, `products.${source}`),
  wire: 'options',
})
const relation = (risk: boolean): FilterField[] => [
  multi('industries', 'Indústria', 'relationship.industries'),
  multi('distributors', 'Distribuidor', 'relationship.distributors'),
  text('cnpj', 'CNPJ Lojista'),
  text('nomeLojista', 'Nome Lojista'),
  multi('estadoLojista', 'Estado Lojista', 'relationship.estados'),
  multi('cidadeLojista', 'Cidade Lojista', 'relationship.cidades'),
  multi('segmentoLojista', 'Segmento Lojista', 'relationship.segmentos'),
  multi('tamanhoUL', 'Tamanho UL', 'relationship.tamanhosUL'),
  text('valorUltimoPedidoMin', 'Mínimo (R$)', 'Valor do último pedido'),
  text('valorUltimoPedidoMax', 'Máximo (R$)', 'Valor do último pedido'),
  flag('apenasUnilever90Dias', 'Cliente exclusivo UL', 'Sim', 'Nao'),
  ...(risk ? [multi('faixaRisco', 'Risco de Churn', 'relationship.faixasRisco')] : []),
]
const months =
  'Janeiro Fevereiro Março Abril Maio Junho Julho Agosto Setembro Outubro Novembro Dezembro'
    .split(' ')
    .map((label, index) => ({value: String(index + 1).padStart(2, '0'), label}))
const annual = (): FilterField[] => [
  {
    ...multi('month', 'Meses', undefined, months, 'Período'),
    initial: [String(new Date().getMonth() + 1).padStart(2, '0')],
  },
  {
    ...multi(
      'year',
      'Anos',
      undefined,
      choices(
        Array.from({length: new Date().getFullYear() - 2019}, (_, index) =>
          String(new Date().getFullYear() - index)
        )
      ),
      'Período'
    ),
    initial: [String(new Date().getFullYear())],
  },
]
const history: ReportFilterSchema = {
  title: 'Histórico Preço Estoque',
  fields: [
    reference(
      [
        {value: 'DataPreco', label: 'Data Preço'},
        {value: 'DataEstoque', label: 'Data Estoque'},
      ],
      'DataPreco'
    ),
    ...period,
    text('produto', 'Nome do Produto'),
    text('ean', 'EAN'),
    text('marca', 'Marca'),
    text('politicaPreco', 'Política de Preço'),
    distributor,
    industry,
  ],
}
const invoice: ReportFilterSchema = {
  title: 'Nota Fiscal',
  fields: [
    reference(),
    ...period,
    cnpj,
    text('idPedido', 'ID do Pedido'),
    text('meioPagamento', 'Meio de Pagamento'),
    text('prazoPagamento', 'Prazo de Pagamento'),
    distributor,
    industry,
    region,
    flag('dataNFIntegracao', 'Data Nota Fiscal Integração'),
    flag('dataFaturadoIntegracao', 'Data Faturado Integração'),
  ],
}

export const REPORT_FILTER_SCHEMAS: Record<ReportType, ReportFilterSchema> = {
  pedidos: {title: 'Pedidos', fields: orderFields()},
  promocoes: {title: 'Promoções', fields: orderFields(true)},
  notafiscal: invoice,
  nota_fiscal: invoice,
  preco_estoque_hist: history,
  ultimo_preco_estoque: {
    title: 'Último Preço Estoque',
    fields: [
      {key: 'filterByDate', label: 'Filtrar por data', kind: 'toggle', section: 'Período'},
      ...period.map((field) => ({
        ...field,
        when: (values: Record<string, any>) => values.filterByDate === 'SIM',
      })),
      industry,
      distributor,
      text('idSKU', 'SKU'),
      text('ean', 'EAN'),
      text('produto', 'Nome do Produto'),
      product('familiaProduto', 'Família do Produto', 'familia'),
      product('marca', 'Marca'),
      product('grupo', 'Grupo'),
      text('politicaPreco', 'Política de Preço'),
      product('tipoPreco', 'Tipo de Preço'),
      text('valorPreco', 'Valor do Preço'),
      text('estoque', 'Estoque'),
      multi('statusEstoque', 'Status do Estoque', undefined, choices(['In Stock', 'Out of Stock'])),
    ],
  },
  distribuidor_prioritario: {
    title: 'Distribuidor Prioritário',
    fields: [
      distributor,
      industry,
      region,
      bu,
      text('idZona', 'IDZona'),
      text('cepInicial', 'CEP Inicial'),
      text('cepFinal', 'CEP Final'),
      text('segmento', 'Segmento'),
      UF,
      text('municipio', 'Município'),
    ],
  },
  cadastro_lojistas: {
    title: 'Cadastro Lojistas',
    fields: [
      multi('status', 'Status', undefined, [
        {value: 'PREAPV', label: 'Pré-Aprovado'},
        {value: 'APV', label: 'Aprovado'},
        {value: 'RPV', label: 'Reprovado'},
      ]),
      cnpj,
      text('razaoSocial', 'Razão Social'),
      distributor,
      industry,
      bu,
      UF,
      text('cidade', 'Cidade'),
      text('idRede', 'ID Rede'),
      text('idGrupoEconomico', 'ID Grupo Econômico'),
      text('politicaPreco', 'Política de Preço'),
      flag('isBlockList', 'Blocklist'),
    ],
  },
  vendedor_distribuidor: {
    title: 'Cadastro Vendedores',
    fields: [
      multi('status', 'Status', undefined, choices(['Ativo', 'Inativo'])),
      distributor,
      text('name', 'Nome do Vendedor'),
      text('cpfVendedor', 'CPF Vendedor'),
      text('emailVendedor', 'E-mail do Vendedor'),
    ],
  },
  politica_preco: {
    title: 'Política de Preço',
    fields: [
      industry,
      distributor,
      text('idSKU', 'SKU'),
      bu,
      cnpj,
      product('marca', 'Marca'),
      text('politicaPreco', 'Política de Preço'),
      product('tipoPreco', 'Tipo de Preço'),
      text('prazoPagamento', 'Prazo de Pagamento'),
      flag('isBlockList', 'FL BlockList'),
    ],
  },
  apuracao: {
    title: 'Apuração',
    fields: [
      reference(
        [
          {value: 'apuracao', label: 'Apuração'},
          {value: 'cancelados', label: 'Cancelados'},
        ],
        'apuracao'
      ),
      region,
      ...annual(),
    ],
  },
  sellout: {title: 'Sellout', fields: [region, ...annual()]},
  cupons: {
    title: 'Cashback Cupons',
    fields: [text('nomeCupom', 'Nome do Cupom'), flag('cupomUtilizado', 'Cupom Utilizado')],
  },
  lojistas: {
    title: 'Cashback Lojistas',
    monthly: true,
    fields: [
      ...period,
      text('nomeCupom', 'Nome do Cupom'),
      text('regraPromocao', 'ID Regra Promoção'),
      text('name', 'Razão Social'),
      cnpj,
      flag('cupomUtilizado', 'Cupom Utilizado', '1', '0'),
    ],
  },
  lojas_sem_compra_12_meses: {title: 'Lojas Venda Premiada', fields: [cnpj, distributor, industry]},
  lojas_quase_churn: {title: 'Lojas quase Churn', fields: relation(true)},
  lojas_quase_fidelizadas: {title: 'Lojas Quase Fidelizadas', fields: relation(false)},
}
